<template>
  <div class="manage-section">
    <div class="manage-header">
      <div class="manage-tabs">
        <button class="manage-tab" :class="{ active: currentManageTab === 'chat' }" @click="switchManageTab('chat')">
          <i class="el-icon-chat-dot-round"></i>
          聊天
        </button>
        <button class="manage-tab" :class="{ active: currentManageTab === 'mail' }" @click="switchManageTab('mail')">
          <i class="el-icon-message"></i>
          邮件
        </button>
      </div>
      <div class="manage-actions">
        <div class="manage-filters">
          <label class="toggle-label">
            <input type="checkbox" v-model="showStarredOnly" @change="handleStarredFilterChange">
            <span class="toggle-text">只显示收藏</span>
          </label>
        </div>
        <button class="delete-btn" :disabled="!hasSelectedItems" @click="handleDelete">
          <i class="el-icon-delete"></i>
          删除选中
        </button>
      </div>
    </div>

    <div class="manage-content">
      <!-- 初始加载动画 -->
      <div v-if="initialLoading" class="initial-loading-container">
        <div class="loading-spinner">
          <i class="el-icon-loading"></i>
          <span>正在加载数据...</span>
        </div>
      </div>

      <!-- 列表内容 -->
      <div v-else class="manage-list-container">
        <div class="manage-list-header">
          <div class="manage-header-col">
            <div class="checkbox-wrapper">
              <input type="checkbox" v-model="selectAll" @change="handleSelectAll">
              <span class="checkmark"></span>
            </div>
            创建时间
          </div>
          <div class="manage-header-col">性别</div>
          <div class="manage-header-col">消息</div>
          <div class="manage-header-col">{{ currentManageTab === 'chat' ? '心情' : '收藏' }}</div>
        </div>
        <div class="manage-list-body">
          <div class="manage-list-item" v-for="item in filteredManagedIcebreakers" :key="item.id">
            <div class="manage-item-col created-col">
              <div class="checkbox-wrapper">
                <input type="checkbox" v-model="item.selected" @change="handleItemSelect">
                <span class="checkmark"></span>
              </div>
              <div class="created-date">
                <div class="date-part">{{ formatDateOnly(item.createdAt) }}</div>
                <div class="time-part">{{ formatTimeOnly(item.createdAt) }}</div>
              </div>
            </div>
            <div class="manage-item-col gender-col">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="manage-item-col message-col">
              <div class="manage-message-text">{{ item.message }}</div>
              <!-- 图片附件显示 -->
              <div v-if="item.attachments && item.attachments.length > 0" class="attachment-images">
                <div v-for="(attachment, index) in item.attachments" :key="index" class="attachment-item">
                  <div v-if="attachment.type === 'photo'" class="image-attachment">
                    <imagePreview :src="attachment.url" :alt="`附件图片 ${index + 1}`"
                      :previewSrcList="getImageUrls(item.attachments)" :currentIndex="index" class="attachment-image" />
                  </div>
                </div>
              </div>
            </div>
            <div class="manage-item-col moods-col">
              <div class="mood-tags" v-if="currentManageTab === 'chat'">
                <div class="mood-tag-wrapper" v-for="v in item.moods" :key="v.type">
                  <span class="mood-tag" :class="v.class">
                    <i :class="v.icon"></i>
                    {{ v.label }}
                  </span>
                  <button class="star-btn" :class="{ starred: v.isStarred }" @click="toggleStar(item, v)">
                    <i :class="v.isStarred ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                  </button>
                </div>
              </div>
              <div class="mail-star-wrapper" v-else style="justify-content: flex-start;">
                <button class="star-btn" :class="{ starred: item.starred }" @click="toggleStar(item)">
                  <!-- el-icon-star-on -->
                  <i :class="item.starred ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading动画 -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner">
            <i class="el-icon-loading"></i>
            <span>加载中...</span>
          </div>
        </div>

        <!-- 没有更多数据提示 -->
        <div v-if="!loading && !hasMore && managedIcebreakers.length > 0" class="no-more-data">
          <span>没有更多数据了</span>
        </div>
      </div>

      <!-- 删除确认对话框 -->
      <div v-if="showDeleteConfirm" class="delete-confirm-overlay" @click="cancelDelete">
        <div class="delete-confirm-modal" @click.stop>
          <div class="delete-confirm-header">
            <i class="el-icon-warning"></i>
            <span>确认删除</span>
          </div>
          <div class="delete-confirm-content">
            <p>您确定要删除选中的 {{ selectedCount }} 条记录吗？</p>
            <p class="delete-warning">此操作不可撤销，请谨慎操作。</p>
          </div>
          <div class="delete-confirm-actions">
            <button class="cancel-btn" @click="cancelDelete">取消</button>
            <button class="confirm-delete-btn" @click="confirmDelete" :disabled="deleting">
              <i v-if="deleting" class="el-icon-loading"></i>
              {{ deleting ? '删除中...' : '确认删除' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getIcebreakersList, starIcebreakers, unstarIcebreakers, deleteIcebreakers } from '@/api/talkytimes/icebreakers'
import imagePreview from '@/components/imagePreview.vue'
export default {
  name: 'ManageIcebreaker',
  components: {
    imagePreview
  },
  props: {
    selectedUser: {
      type: Object,
      default: null
    }
  },
  watch: {
    selectedUser: {
      handler(newVal) {
        if (newVal) {
          this.getIcebreakersList()
        }
      },
      immediate: true
    }
  },
  data() {
    return {
      currentManageTab: 'chat',
      showStarredOnly: false,
      selectAll: false,
      loading: false,
      initialLoading: false,
      hasMore: true,
      lastId: 0,
      managedIcebreakers: [],
      showDeleteConfirm: false,
      deleting: false,
      selectedCount: 0
    }
  },
  watch: {
    selectedUser: {
      handler(newUser, oldUser) {
        // 当用户切换时，重新获取管理数据
        if (newUser && newUser !== oldUser) {
          console.log('ManageIcebreaker: 用户切换，重新获取管理数据', newUser)
          this.resetManageData()
          this.getIcebreakersList()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.getIcebreakersList()
    this.addScrollListener()
  },
  beforeDestroy() {
    this.removeScrollListener()
  },
  computed: {
    filteredManagedIcebreakers() {
      // 直接返回API返回的数据，不需要前端过滤
      return Array.isArray(this.managedIcebreakers) ? this.managedIcebreakers : []
    },
    hasSelectedItems() {
      return this.managedIcebreakers.some(item => item.selected);
    }
  },
  methods: {
    switchManageTab(tab) {
      this.currentManageTab = tab
      this.lastId = 0
      this.hasMore = true
      this.getIcebreakersList()
    },
    async toggleStar(item, mood) {
      console.log(item, mood, 'item,mood')
      try {
        if (this.currentManageTab === 'chat') {
          // 消息版块 - 每个心情都有独立的收藏状态
          if (!mood.isStarred) {
            // 未收藏，调用收藏接口
            await starIcebreakers({
              idIcebreaker: item.id,
              type: item.type,
              mood: mood.code
            })
            // 更新本地状态
            mood.isStarred = true
          } else {
            // 已收藏，调用取消收藏接口
            await unstarIcebreakers({
              idIcebreaker: item.id,
              type: item.type,
              mood: mood.code
            })
            // 更新本地状态
            mood.isStarred = false
          }
        } else {
          // 邮件版块
          if (!item.starred) {
            // 未收藏，调用收藏接口
            await starIcebreakers({
              idIcebreaker: item.id,
              type: item.type
            })
            // 更新本地状态
            item.starred = true
          } else {
            // 已收藏，调用取消收藏接口
            await unstarIcebreakers({
              idIcebreaker: item.id,
              type: item.type
            })
            // 更新本地状态
            item.starred = false
          }
        }
      } catch (error) {
        console.error('Error toggling star:', error)
        // 如果API调用失败，不更新本地状态
      }
    },
    handleSelectAll() {
      if (!Array.isArray(this.managedIcebreakers)) {
        return
      }
      this.managedIcebreakers.forEach(item => {
        item.selected = this.selectAll
      })
    },
    handleItemSelect() {
      if (!Array.isArray(this.managedIcebreakers)) {
        this.selectAll = false
        return
      }
      const selectedCount = this.managedIcebreakers.filter(item => item.selected).length
      this.selectAll = selectedCount === this.managedIcebreakers.length && selectedCount > 0
    },
    handleStarredFilterChange() {
      this.lastId = 0
      this.hasMore = true
      this.getIcebreakersList()
    },
    async getIcebreakersList(isLoadMore = false) {
      if (this.loading) return

      // 确保有选中的用户
      if (!this.selectedUser || !this.selectedUser.id) {
        console.error('没有选中用户，无法获取管理列表数据')
        this.$message.error('请先选择一个用户')
        return
      }

      if (isLoadMore) {
        this.loading = true
      } else {
        this.initialLoading = true
      }

      // 根据图片1和图片2的参数格式构建请求参数
      const params = {
        idTrustedUser: this.selectedUser.id,
        idLast: isLoadMore ? this.lastId : 0,
        limit: 15,
        statuses: ["approved"],
        isLaunched: false,
        isStarred: this.showStarredOnly
      }

      // 根据当前标签设置types参数
      if (this.currentManageTab === 'chat') {
        params.types = ["message"]
      } else if (this.currentManageTab === 'mail') {
        params.types = ["mail"]
      }

      try {
        const response = await getIcebreakersList(params)
        // 根据API返回的数据结构处理数据
        if (response.data && response.data.items) {
          const newItems = response.data.items.map(item => ({
            id: item.id,
            createdAt: new Date(item.dateCreated),
            type: item.type,
            message: item.content,
            attachments: item.attachments || [],
            moods: item.moods ? item.moods
              .filter(mood => mood.status !== 'declined') // 过滤掉 status 为 declined 的心情
              .map(mood => ({
                code: mood.code,
                type: mood.code,
                label: this.getMoodLabel(mood.code),
                class: this.getMoodClass(mood.code),
                icon: this.getMoodIcon(mood.code),
                isStarred: mood.isStarred,
                status: mood.status // 保留 status 字段
              })) : [],
            starred: item.isStarred,
            selected: false
          }))

          if (isLoadMore) {
            // 加载更多时追加数据
            this.managedIcebreakers = [...this.managedIcebreakers, ...newItems]
          } else {
            // 首次加载或切换标签时替换数据
            this.managedIcebreakers = newItems
          }

          // 判断是否还有更多数据
          this.hasMore = newItems.length === 15
          if (newItems.length > 0) {
            this.lastId = newItems[newItems.length - 1].id
          }
        } else {
          if (!isLoadMore) {
            this.managedIcebreakers = []
          }
          this.hasMore = false
        }
      } catch (error) {
        console.error('Error fetching icebreakers:', error)
        if (!isLoadMore) {
          this.managedIcebreakers = []
        }
        this.hasMore = false
      } finally {
        if (isLoadMore) {
          this.loading = false
        } else {
          this.initialLoading = false
        }
      }
    },
    // 重置管理数据
    resetManageData() {
      // 清空数据
      this.managedIcebreakers = []

      // 重置分页状态
      this.lastId = 0
      this.hasMore = true

      // 重置加载状态
      this.loading = false
      this.initialLoading = false

      // 重置选择状态
      this.selectAll = false
      this.selectedCount = 0
    },
    formatDateOnly(date) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric'
      }).format(date)
    },
    formatTimeOnly(date) {
      return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date)
    },
    getMoodLabel(code) {
      const moodLabels = {
        'hot_talks': 'Hot talks',
        'real_love': 'Real love',
        'romance': 'Romance',
        'flirt': 'Flirt',
        'friendship': 'Friendship',
        'not_sure': 'Not sure'
      }
      return moodLabels[code] || code
    },
    getMoodClass(code) {
      const moodClasses = {
        'hot_talks': 'hot-talks',
        'real_love': 'real-love',
        'romance': 'romance',
        'flirt': 'flirt',
        'friendship': 'friendship',
        'not_sure': 'not-sure'
      }
      return moodClasses[code] || 'default'
    },
    getMoodIcon(code) {
      const moodIcons = {
        'hot_talks': '💋',
        'real_love': '🕊️',
        'romance': '💕',
        'flirt': '😘',
        'friendship': '🤝',
        'not_sure': '🤔'
      }
      return moodIcons[code] || '💬'
    },
    getImageUrls(attachments) {
      if (!attachments || !Array.isArray(attachments)) {
        return []
      }
      return attachments
        .filter(attachment => attachment.type === 'photo' && attachment.url)
        .map(attachment => attachment.url)
    },
    addScrollListener() {
      this.scrollHandler = this.handleScroll.bind(this)
      window.addEventListener('scroll', this.scrollHandler)
    },
    removeScrollListener() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler)
      }
    },
    handleScroll() {
      if (this.loading || !this.hasMore) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      // 当滚动到距离底部100px时触发加载更多
      if (scrollTop + windowHeight >= documentHeight - 100) {
        this.getIcebreakersList(true)
      }
    },
    async handleDelete() {
      this.showDeleteConfirm = true;
      this.selectedCount = this.managedIcebreakers.filter(item => item.selected).length;
    },
    cancelDelete() {
      this.showDeleteConfirm = false;
      this.selectedCount = 0;
    },
    async confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      try {
        const selectedItems = this.managedIcebreakers.filter(item => item.selected);
        const idsToDelete = selectedItems.map(item => item.id);
        console.log(idsToDelete, 'idsToDelete')
        // return
        await deleteIcebreakers({
          idsIcebreakers: idsToDelete
        });

        // 从本地数据中移除已删除的项
        this.managedIcebreakers = this.managedIcebreakers.filter(item => !selectedItems.some(selected => selected.id === item.id));
        this.selectAll = false; // 取消全选
        this.showDeleteConfirm = false;
        this.selectedCount = 0;
        this.lastId = 0; // 重置lastId，以便加载更多
        this.hasMore = true;
        this.getIcebreakersList();

        // 显示成功提示
        this.$message.success(`成功删除 ${selectedItems.length} 条记录`);

      } catch (error) {
        console.error('Error deleting icebreakers:', error);
        // 显示错误提示
        this.$message.error('删除失败，请重试');
      } finally {
        this.deleting = false;
      }
    }
  }
}
</script>

<style scoped lang="scss">
.manage-section {
  background: white;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100vh;
}

.manage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  flex-shrink: 0;
}

.manage-tabs {
  display: flex;
  gap: 32px;
}

.manage-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #bebebe;
  padding: 8px 0;
  position: relative;
}

.manage-tab.active {
  color: #3730a3;
  font-weight: 600;
}

.manage-tab.active::after {
  content: '';
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #3730a3 0%, #581c87 100%);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(55, 48, 163, 0.4);
}

.manage-filters {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #64748b;
}

.toggle-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3730a3;
}

.toggle-text {
  font-weight: 500;
}

.manage-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.delete-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #dc2626;
  }

  &:disabled {
    background: #d1d5db;
    color: #9ca3af;
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.manage-content {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.manage-list-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.manage-list-header {
  display: grid;
  grid-template-columns: 140px 60px minmax(350px, 1fr) 200px;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
  font-weight: 700;
  font-size: 14px;
  color: #1f2937;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.manage-list-body {
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
}

.manage-list-item {
  display: grid;
  grid-template-columns: 140px 60px minmax(350px, 1fr) 200px;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  align-items: flex-start;
}

.manage-list-item:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.manage-item-col {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.manage-header-col {
  font-size: 14px;
  color: #1f2937;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.created-col {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 120px;
}

.checkbox-wrapper {
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-wrapper input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 18px;
  width: 18px;
  z-index: 1;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 18px;
  width: 18px;
  background-color: white;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  pointer-events: none;
}

.checkbox-wrapper:hover .checkmark {
  border-color: #3730a3;
}

.checkbox-wrapper input:checked~.checkmark {
  background: linear-gradient(135deg, #3730a3 0%, #581c87 100%);
  border-color: #3730a3;
}

.checkbox-wrapper input:checked~.checkmark:after {
  display: block;
}

.checkbox-wrapper .checkmark:after {
  left: 6px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  content: "";
  position: absolute;
  display: none;
}

.created-date {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 80px;
}

.date-part {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.2;
}

.time-part {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.2;
}

.gender-col {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.gender-col i {
  font-size: 16px;
  color: #6b7280;
}

.message-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 80px;
}

.manage-message-text {
  line-height: 1.5;
  font-weight: 500;
  font-size: 14px;
  max-height: 60px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 8px;
}

.attachment-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.attachment-item {
  flex-shrink: 0;
}

.image-attachment {
  position: relative;
}

.attachment-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  cursor: pointer;
  overflow: hidden;
}

.attachment-image:hover {
  border-color: #3730a3;
}

.moods-col {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.mood-tags {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
  align-items: flex-start;
  justify-content: flex-start;
}

.mood-tag-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  width: 100%;
}

.mail-star-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 6px;
  width: 100%;
}

.mood-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: #6b7280;
  border: 1px solid rgba(255, 255, 255, 0.3);
  white-space: nowrap;
  flex-shrink: 0;
  flex: 1;
}

.mood-tag i {
  font-size: 12px;
}

.mood-tag.hot-talks {
  background: #ec4899;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.mood-tag.real-love {
  background: #3b82f6;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.mood-tag.friendship {
  background: #10b981;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.mood-tag.romance {
  background: #f59e0b;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.mood-tag.flirt {
  background: #8b5cf6;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.mood-tag.not-sure {
  background: #6b7280;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.mood-tag.default {
  background: #6b7280;
  border: 1px solid rgba(255, 255, 255, 0.3);
}



.star-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #d1d5db;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.star-btn:hover {
  color: #fbbf24;
}

.star-btn.starred {
  color: #fbbf24;
}

.star-btn.starred:hover {
  color: #d1d5db;
}

.initial-loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  background: white;
  min-height: 200px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: white;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 14px;
}

.loading-spinner i {
  font-size: 18px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.no-more-data {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: white;
  color: #9ca3af;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {

  .manage-list-header,
  .manage-list-item {
    grid-template-columns: 70px 50px 1fr 120px;
    gap: 12px;
    padding: 12px 16px;
  }

  .manage-message-text {
    font-size: 13px;
  }

  .mood-tag {
    padding: 4px 8px;
    font-size: 11px;
  }

  .star-btn {
    font-size: 16px;
  }

  .attachment-image {
    width: 50px;
    height: 50px;
  }

  .attachment-images {
    gap: 6px;
  }
}

/* 删除确认对话框样式 */
.delete-confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.delete-confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.delete-confirm-header {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ef4444;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
}

.delete-confirm-header i {
  font-size: 24px;
}

.delete-confirm-content {
  margin-bottom: 24px;
  color: #4b5563;
  font-size: 16px;
  line-height: 1.6;
}

.delete-warning {
  color: #9ca3af;
  font-size: 14px;
  margin-top: 8px;
}

.delete-confirm-actions {
  display: flex;
  justify-content: space-around;
  width: 100%;
}

.cancel-btn,
.confirm-delete-btn {
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  white-space: nowrap;
}

.cancel-btn {
  background: #d1d5db;
  color: #4b5563;
  margin-right: 10px;

  &:hover:not(:disabled) {
    background: #9ca3af;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.confirm-delete-btn {
  background: #ef4444;
  color: white;
  margin-left: 10px;

  &:hover:not(:disabled) {
    background: #dc2626;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
</style>